import {
	Box,
	Button,
	CircularProgress,
	Divider,
	FormControl,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	Tooltip,
	styled,
	Typography,
	Autocomplete,
	TextField,
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import PageTitle from "../../components/PageTitle/PageTitle";
import "./Analytics.css";
import ECharts from "../../components/ECharts/ECharts";

import { useAuth } from "../../context/authContext";
import { useSite } from "../../context/siteContext";
import NotAuthorized from "../../components/NotAuthorized/NotAuthorized";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import AnalyticsCharts from "../../components/AnalyticsCharts/AnalyticsCharts";
import DateRange from "../../components/DateRange/DateRange";
import FilterBox from "../../components/FilterBox/FilterBox";
import AnalyticsTables from "../../components/AnalyticsTables/AnalyticsTables";
import HomeIcon from "@mui/icons-material/Home";
import CloseIcon from "@mui/icons-material/Close";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { BUTTON, DATE_RANGES, LABELS, MENU_ITEMS, TITLES, UI_TEXT } from "../../constants";
import { useTranslation } from "react-i18next";
import { getAllUsers, getMachinesByProvision } from "../../services/analyticsService";
import { getCustomersCusSites } from "../../services/manageCustomerMasterService";
import { getAllCustomerSites } from "../../services/manageSitesMasterService";
import { getUserAllocations } from "../../services/manageSitesService";
import { useOptions } from "../../context/optionContext";
import { resolveEnabledAdminModules } from "../../utilities/adminModules";
// import { DateRangePicker } from "react-date-range";
import { alpha } from "@mui/system";
const getCurrentDate = () => new Date();
const formatDate = (date) => date.toLocaleDateString();
const formatApiDate = (date) => {
	if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
		return "";
	}

	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
};

// Get the start of the current week (Monday)
const getStartOfWeek = (date) => {
	const day = date.getDay(),
		diff = date.getDate() - day + (day == 0 ? -6 : 1); // adjust when day is sunday
	date.setDate(diff);
	date.setHours(0, 0, 0, 0);
	return date;
};

// Get the end of the current week (Sunday)
const getEndOfWeek = (date) => {
	const startOfWeek = getStartOfWeek(date);
	startOfWeek.setDate(startOfWeek.getDate() + 6);
	startOfWeek.setHours(23, 59, 59, 999);
	return startOfWeek;
};

// Get the start and end of the current month
const getStartOfMonth = (date) => {
	date.setDate(1);
	date.setHours(0, 0, 0, 0);
	return date;
};

const getEndOfMonth = (date) => {
	date.setMonth(date.getMonth() + 1);
	date.setDate(0);
	date.setHours(23, 59, 59, 999);
	return date;
};

// Get the start and end of the current quarter
const getStartOfQuarter = (date) => {
	const currentMonth = date.getMonth();
	let startMonth;
	if (currentMonth <= 2) startMonth = 0; // January to March (Q1)
	else if (currentMonth <= 5) startMonth = 3; // April to June (Q2)
	else if (currentMonth <= 8) startMonth = 6; // July to September (Q3)
	else startMonth = 9; // October to December (Q4)

	date.setMonth(startMonth);
	date.setDate(1);
	date.setHours(0, 0, 0, 0);
	return date;
};

const getEndOfQuarter = (date) => {
	const currentMonth = date.getMonth();
	let endMonth;
	if (currentMonth <= 2) endMonth = 2; // Q1
	else if (currentMonth <= 5) endMonth = 5; // Q2
	else if (currentMonth <= 8) endMonth = 8; // Q3
	else endMonth = 11; // Q4

	date.setMonth(endMonth + 1);
	date.setDate(0);
	date.setHours(23, 59, 59, 999);
	return date;
};

// Get the start and end of the current year
const getStartOfYear = (date) => {
	date.setMonth(0);
	date.setDate(1);
	date.setHours(0, 0, 0, 0);
	return date;
};

const getEndOfYear = (date) => {
	date.setMonth(11);
	date.setDate(31);
	date.setHours(23, 59, 59, 999);
	return date;
};

const Analytics = () => {
	const StyledButton = {
		minWidth: "50px",
		"&:hover": {
			backgroundColor: (theme) => theme.palette.primary.main,
			color: "#ffffff",
		},
	};

	const [error, setError] = useState(null);
	const [customers, setCustomers] = useState([]);
	const [customersOptions, setCustomersOptions] = useState([]);
	const [machines, setMachines] = useState([]);
	const [machinesOptions, setMachinesOptions] = useState([]);
	const [additionalFilters, setAdditionalFilters] = useState([]);
	const [sites, setSites] = useState([]);
	const [sitesOptions, setSitesOptions] = useState([]);
	const [users, setUsers] = useState([]);
	const [usersOptions, setUsersOptions] = useState([]);
	const [loading, setLoading] = useState(true);
	const [buttonShow, setButtonShow] = useState(false);
	const [selectedRange, setSelectedRange] = useState({
		key: "W",
		label: "Week",
		startDate: formatDate(getStartOfWeek(new Date())),
		endDate: formatDate(getEndOfWeek(new Date())),
	});
	const [selectedDateRange, setSelectedDateRange] = useState([
		getStartOfWeek(new Date()),
		getEndOfWeek(new Date()),
	]);
	const [customDateRanges, setCustomDateRanges] = useState(false);
	const { user, hasPermission } = useAuth();
	const { selectedSite } = useSite();
	const { t } = useTranslation();
	const { optionKeys } = useOptions();
	const enabledAdminModuleSet = useMemo(() => new Set(resolveEnabledAdminModules(optionKeys)), [optionKeys]);
	const ALL_VALUE = t('MENU_ITEMS.ALL');
	const [filters, setFilters] = useState({
		customer: ALL_VALUE,
		site: ALL_VALUE,
		user: ALL_VALUE,
		region: t('MENU_ITEMS.ALL'),
		machine: ALL_VALUE,
	});

	const buttons = [
		{ key: "T", label: t('DATE_RANGES.TODAY') },
		{ key: "W", label: t('DATE_RANGES.WEEK') },
		{ key: "M", label: t('DATE_RANGES.MONTH') },
		{ key: "Q", label: t('DATE_RANGES.QUARTER') },
		{ key: "Y", label: t('DATE_RANGES.YEAR') },
		// { key: "All", label: "All" },
	];


	const [chartQueryFilter, setChartQueryFilter] = useState({});
	const [showCharts, setShowCharts] = useState(true);
	const [selectedMetric, setSelectedMetric] = useState(null);
	const [accessScope, setAccessScope] = useState({ allowAll: false, allowNone: false });
	const [allocatedSiteIds, setAllocatedSiteIds] = useState([]);

	// Autocomplete search input + debounce
	const [customerInput, setCustomerInput] = useState("");
	const [siteInput, setSiteInput] = useState("");
	const [userInput, setUserInput] = useState("");
	const [machineInput, setMachineInput] = useState("");
	const [debouncedCustomerInput, setDebouncedCustomerInput] = useState("");
	const [debouncedSiteInput, setDebouncedSiteInput] = useState("");
	const [debouncedUserInput, setDebouncedUserInput] = useState("");
	const [debouncedMachineInput, setDebouncedMachineInput] = useState("");

	useEffect(() => { const t = setTimeout(() => setDebouncedCustomerInput(customerInput), 200); return () => clearTimeout(t); }, [customerInput]);
	useEffect(() => { const t = setTimeout(() => setDebouncedSiteInput(siteInput), 200); return () => clearTimeout(t); }, [siteInput]);
	useEffect(() => { const t = setTimeout(() => setDebouncedUserInput(userInput), 200); return () => clearTimeout(t); }, [userInput]);
	useEffect(() => { const t = setTimeout(() => setDebouncedMachineInput(machineInput), 200); return () => clearTimeout(t); }, [machineInput]);

	const normalizeMachineNumber = (value) => {
		if (value == null) return "";
		return String(value).trim().toUpperCase();
	};
	const normalizeEmail = (value) => {
		if (value == null) return "";
		return String(value).trim().toLowerCase();
	};
	const normalizeSiteNumber = (value) => {
		if (value == null) return "";
		return String(value).trim().toUpperCase();
	};
	const normalizeCustomerNumber = (value) => {
		if (value == null) return "";
		return String(value).trim().toUpperCase();
	};
	const uniqueBy = (list, keyFn) => {
		const map = new Map();
		(list || []).forEach((item) => {
			const key = keyFn(item);
			if (!key || map.has(key)) return;
			map.set(key, item);
		});
		return Array.from(map.values());
	};
	const isAllValue = (value) => value == null || value === ALL_VALUE;
	const allOption = useMemo(() => {
		const label = t("MENU_ITEMS.ALL");
		return { value: ALL_VALUE, label, searchText: label.toLowerCase() };
	}, [t]);
	const withAllOption = useCallback(
		(options = []) => {
			const filtered = options.filter((opt) => opt?.value !== ALL_VALUE);
			return [allOption, ...filtered];
		},
		[allOption]
	);

	// Efficient filtering for large option lists (debounced, capped)
	const cap = 500;
	const getSearchQuery = (inputValue, selectedOption) => {
		const query = (inputValue || "").toLowerCase();
		if (!query) return "";
		const selectedLabel = (selectedOption?.label || "").toLowerCase();
		return query === selectedLabel ? "" : query;
	};

	const selectedCustomerOption = customersOptions.find(o => o.value === filters.customer);
	const selectedSiteOption = sitesOptions.find(o => o.value === filters.site);
	const selectedUserOption = usersOptions.find(o => o.value === filters.user);
	const selectedMachineOption = machinesOptions.find(o => o.value === filters.machine);
	const displayedCustomerOptions = useMemo(() => {
		const base = customersOptions || [];
		const q = getSearchQuery(debouncedCustomerInput, selectedCustomerOption);
		const filtered = q
			? base.filter(o => (o.searchText || o.label || "").includes(q))
			: base;
		return filtered.slice(0, cap);
	}, [customersOptions, debouncedCustomerInput, selectedCustomerOption]);

	const displayedSiteOptions = useMemo(() => {
		const base = sitesOptions || [];
		const q = getSearchQuery(debouncedSiteInput, selectedSiteOption);
		const filtered = q
			? base.filter(o => (o.searchText || o.label || "").includes(q))
			: base;
		return filtered.slice(0, cap);
	}, [sitesOptions, debouncedSiteInput, selectedSiteOption]);

	const displayedUserOptions = useMemo(() => {
		const base = usersOptions || [];
		const q = getSearchQuery(debouncedUserInput, selectedUserOption);
		const filtered = q
			? base.filter(o => (o.searchText || o.label || "").includes(q))
			: base;
		return filtered.slice(0, cap);
	}, [usersOptions, debouncedUserInput, selectedUserOption]);

	const displayedMachineOptions = useMemo(() => {
		const base = machinesOptions || [];
		const q = getSearchQuery(debouncedMachineInput, selectedMachineOption);
		const filtered = q
			? base.filter(o => (o.searchText || o.label || "").includes(q))
			: base;
		const deduped = [];
		const seen = new Set();
		filtered.forEach((opt) => {
			const key =
				normalizeMachineNumber(opt?.machine_number) ||
				normalizeMachineNumber(opt?.label) ||
				String(opt?.value);
			if (!key || seen.has(key)) return;
			seen.add(key);
			deduped.push(opt);
		});
		return deduped.slice(0, cap);
	}, [machinesOptions, debouncedMachineInput, selectedMachineOption]);

	const appliedSelectedSiteRef = useRef(false);

	const isAdminUser = useMemo(() => {
		if (!user) return false;
		if (user.user_id === 1) return true;
		const roleToken =
			user?.role?.role_type ||
			user?.role?.role_name ||
			user?.role?.role ||
			user?.user_role_name ||
			user?.user_role ||
			"";
		return String(roleToken).trim().toLowerCase() === "admin";
	}, [user]);

	useEffect(() => {
		const fetchUsers = async (scope) => {
			try {
				if (scope.allowNone) {
					setUsers([]);
					return;
				}
				const response = await getAllUsers();
				if (response.data.length > 0) {
					const dedupedUsers = uniqueBy(response.data, (u) => normalizeEmail(u?.user_email) || String(u?.user_id));
					setUsers(dedupedUsers);
				} else {
					setError(`Failed to fetch users: No users found`);
				}
			} catch (err) {
				setError(`Failed to fetch users from: ${err.message}`);
			}
		};

		const fetchCustomers = async (scope) => {
			try {
				if (scope.allowNone) {
					setCustomers([]);
					return;
				}
				const response = await getCustomersCusSites();
				if (response.status) {
					const allCustomers = response.data.filter(
						(customer) => customer.customer_name && customer.customer_name !== "EMPTY"
					);
					const dedupedCustomers = uniqueBy(allCustomers, (c) =>
						normalizeCustomerNumber(c?.customer_number) || String(c?.cus_id)
					);
					setCustomers(dedupedCustomers);
				} else {
					setError("Failed to fetch customers.");
				}
			} catch (err) {
				setError("Failed to fetch customers: " + err.message);
			}
		};
		const fetchSites = async (siteIds, scope) => {
			try {
				if (scope.allowNone) {
					setSites([]);
					return;
				}
				const response = await getAllCustomerSites();
				if (response.status) {
					const allSites = response.data.filter(
						(customer) => customer.customer_site_name && customer.customer_site_name !== "EMPTY"
					);
					const dedupedSites = uniqueBy(allSites, (s) =>
						normalizeSiteNumber(s?.customer_site_number || s?.site_number) || String(s?.site_id)
					);
					const filteredSites = scope.allowAll
						? dedupedSites
						: siteIds.length > 0
							? dedupedSites.filter((s) => siteIds.includes(Number(s.site_id)))
							: [];
					setSites(filteredSites);
				} else {
					setError("Failed to fetch customers.");
				}
			} catch (err) {
				setError("Failed to fetch customers: " + err.message);
			}
		};

		const fetchOrders = async () => { };

		const fetchMachines = async (scope) => {
			try {
				if (scope.allowNone) {
					setMachines([]);
					return;
				}
				const response = await getMachinesByProvision();
				if (response.status) {
					const allMachines = (response.data || []).filter(
						(machine) => String(machine?.machine_status || "").toLowerCase() === "active"
					);
					setMachines(allMachines);
				} else {
					setError("Failed to fetch machines.");
				}
			} catch (error) {
				console.error("Error fetching machines:", error);
				setError("Failed to fetch machines.");
			}
		};

		const init = async () => {
			if (!user) return;
			setLoading(true);
			try {
				const uid = user.user_id;
				const allocResp = await getUserAllocations(uid);
				const allocations = allocResp?.data?.allocations || [];
				const siteIds = [...new Set(allocations.map((a) => Number(a.site_id)).filter(Boolean))];
				const cusIds = [...new Set(allocations.map((a) => Number(a.cus_id)).filter(Boolean))];
				const hasAllocations = siteIds.length > 0 || cusIds.length > 0;
				const scope = {
					allowAll: isAdminUser && !hasAllocations,
					allowNone: !isAdminUser && !hasAllocations,
				};
				setAccessScope(scope);
				setAllocatedSiteIds(siteIds);
				await fetchSites(siteIds, scope);
				await fetchUsers(scope);
				await fetchCustomers(scope);
				await fetchMachines(scope);
			} catch (e) {
				const scope = {
					allowAll: Boolean(isAdminUser),
					allowNone: !isAdminUser,
				};
				setAccessScope(scope);
				setAllocatedSiteIds([]);
				await fetchSites([], scope);
				await fetchUsers(scope);
				await fetchCustomers(scope);
				await fetchMachines(scope);
			}
		};

		init();
	}, []);

	useEffect(() => {
		const hasFilters =
			!isAllValue(filters.customer) ||
			!isAllValue(filters.site) ||
			!isAllValue(filters.user) ||
			!isAllValue(filters.machine);
		setButtonShow(hasFilters);
	}, [filters.customer, filters.site, filters.user, filters.machine]);

	const appliedDropdownFilters = useMemo(() => {
		const chips = [];
		const customerLabel = customersOptions.find(o => o.value === filters.customer)?.label;
		const siteLabel = sitesOptions.find(o => o.value === filters.site)?.label;
		const userLabel = usersOptions.find(o => o.value === filters.user)?.label;
		const machineLabel = machinesOptions.find(o => o.value === filters.machine)?.label;
		if (customerLabel && !isAllValue(filters.customer)) chips.push({ id: "customer", label: customerLabel });
		if (siteLabel && !isAllValue(filters.site)) chips.push({ id: "site", label: siteLabel });
		if (userLabel && !isAllValue(filters.user)) chips.push({ id: "user", label: userLabel });
		if (machineLabel && !isAllValue(filters.machine)) chips.push({ id: "machine", label: machineLabel });
		return chips;
	}, [
		filters.customer,
		filters.site,
		filters.user,
		filters.machine,
		customersOptions,
		sitesOptions,
		usersOptions,
		machinesOptions,
	]);

	// Helper to generate site options
	const getSiteOptions = (sites) =>
		sites.map((site) => ({
			value: site.site_id,
			label: `${site.customer_site_name} - ${site.customer_site_number}`,
			searchText: `${site.customer_site_name} ${site.customer_site_number}`.toLowerCase(),
		}));

	const uniqueMachinesByNumber = (list = []) => {
		const seen = new Map();
		list.forEach((mac) => {
			const key =
				normalizeMachineNumber(mac?.machine_number) ||
				normalizeMachineNumber(mac?.machine_name) ||
				String(mac?.machine_id);
			if (!key || seen.has(key)) return;
			seen.set(key, mac);
		});
		return Array.from(seen.values());
	};

	const getMachineOptions = (machines) =>
		uniqueMachinesByNumber(machines).map((mac) => ({
			key: `${mac.machine_id}_${mac.site_id}_${mac.cus_id}`,
			value: mac.machine_id,
			label: `${mac.machine_name} - ${mac.machine_number}`,
			machine_number: mac.machine_number,
			searchText: `${mac.machine_name} ${mac.machine_number}`.toLowerCase(),
		}));

	const isApprovedUser = (row) => {
		const statusValue = row?.user_status ?? row?.status ?? null;
		if (Number(statusValue) === 1) return true;
		const normalized = String(statusValue || "").trim().toLowerCase();
		return normalized === "approved" || normalized === "active";
	};
	const isAdminRow = (row) => {
		const roleToken =
			row?.user_role_name ||
			row?.user_role ||
			row?.role?.role_name ||
			row?.role?.role_type ||
			row?.user_type ||
			"";
		const normalized = String(roleToken).trim().toLowerCase();
		return normalized === "admin" || normalized.includes("admin");
	};
	const getUserAllocatedSiteIds = (userRow) => {
		if (Array.isArray(userRow?.allocations) && userRow.allocations.length > 0) {
			return Array.from(
				new Set(
					userRow.allocations
						.map((a) => Number(a?.site_id))
						.filter((sid) => Number.isFinite(sid) && sid > 0)
				)
			);
		}
		if (Array.isArray(userRow?.user_sites)) {
			return Array.from(
				new Set(
					userRow.user_sites
						.map((sid) => Number(sid))
						.filter((sid) => Number.isFinite(sid) && sid > 0)
				)
			);
		}
		const rawSites = `${userRow?.user_sites || ""}`.trim();
		if (!rawSites) return [];
		return rawSites
			.split(",")
			.map((sid) => Number(sid))
			.filter((sid) => Number.isFinite(sid) && sid > 0);
	};
	const getAccessibleSiteIdsForUser = (userRow, scopedSites) => {
		const allocatedIds = getUserAllocatedSiteIds(userRow);
		if (allocatedIds.length > 0) return allocatedIds;
		if (isAdminRow(userRow)) return scopedSites.map((site) => Number(site.site_id));
		return [];
	};
	const getCustomerOptions = (customers) =>
		customers.map((cus) => ({
			value: cus.cus_id,
			label: `${cus.customer_name} - ${cus.customer_number}`,
			searchText: `${cus.customer_name} ${cus.customer_number}`.toLowerCase(),
		}));
	const getUserOptions = (usersList) =>
		uniqueBy(
			usersList.map((user) => ({
				value: user.user_id,
				label: `${user.user_display_name} (${user.user_email})`,
				displayName: user.user_display_name,
				email: user.user_email,
				searchText: `${user.user_display_name} ${user.user_email}`.toLowerCase(),
				user_email: user.user_email,
			})),
			(opt) => normalizeEmail(opt?.user_email) || String(opt?.value)
		);

	const baseSites = useMemo(() => {
		if (accessScope.allowNone) return [];
		if (accessScope.allowAll) return sites;
		if (allocatedSiteIds.length > 0) {
			return sites.filter((site) => allocatedSiteIds.includes(Number(site.site_id)));
		}
		return [];
	}, [accessScope.allowAll, accessScope.allowNone, allocatedSiteIds, sites]);

	const baseCustomers = useMemo(() => {
		const customerIds = new Set(
			baseSites
				.map((site) => Number(site?.cus_id))
				.filter((id) => Number.isFinite(id) && id > 0)
		);
		return customers.filter((cus) => customerIds.has(Number(cus.cus_id)));
	}, [baseSites, customers]);

	const areFiltersEqual = (a, b) =>
		a.customer === b.customer &&
		a.site === b.site &&
		a.user === b.user &&
		a.machine === b.machine;

	const getSitesForMachine = (machineId) => {
		const parsedId = Number(machineId);
		if (!Number.isFinite(parsedId)) return [];
		const siteIds = new Set(
			machines
				.filter((mac) => Number(mac.machine_id) === parsedId)
				.map((mac) => Number(mac.site_id))
				.filter((id) => Number.isFinite(id))
		);
		return baseSites.filter((site) => siteIds.has(Number(site.site_id)));
	};

	const computeScopedSites = (draftFilters) => {
		let scopedSites = baseSites;
		if (!isAllValue(draftFilters.site)) {
			const siteId = Number(draftFilters.site);
			scopedSites = scopedSites.filter((site) => Number(site.site_id) === siteId);
		}
		if (!isAllValue(draftFilters.customer)) {
			const customerId = Number(draftFilters.customer);
			scopedSites = scopedSites.filter((site) => Number(site.cus_id) === customerId);
		}
		if (!isAllValue(draftFilters.machine)) {
			const sitesForMachine = getSitesForMachine(draftFilters.machine);
			const machineSiteIds = new Set(sitesForMachine.map((site) => Number(site.site_id)));
			scopedSites = scopedSites.filter((site) => machineSiteIds.has(Number(site.site_id)));
		}
		if (!isAllValue(draftFilters.user)) {
			const selectedUser = users.find((user) => Number(user.user_id) === Number(draftFilters.user));
			if (!selectedUser || !isApprovedUser(selectedUser)) {
				return [];
			}
			const userSiteIds = getAccessibleSiteIdsForUser(selectedUser, baseSites);
			const allowedSiteIds = new Set(userSiteIds);
			scopedSites = scopedSites.filter((site) => allowedSiteIds.has(Number(site.site_id)));
		}
		return scopedSites;
	};

	const buildOptionsFromScope = (scopedSites) => {
		const siteIds = new Set(scopedSites.map((site) => Number(site.site_id)));
		const customerIds = new Set(
			scopedSites
				.map((site) => Number(site.cus_id))
				.filter((id) => Number.isFinite(id) && id > 0)
		);
		const scopedCustomers = baseCustomers.filter((cus) => customerIds.has(Number(cus.cus_id)));
		const scopedMachines = machines.filter((mac) => siteIds.has(Number(mac.site_id)));
		return {
			customerOptions: getCustomerOptions(scopedCustomers),
			siteOptions: getSiteOptions(scopedSites),
			machineOptions: getMachineOptions(scopedMachines),
			userOptions: [],
		};
	};

	const getUserOptionsForScope = (scopedSites, draftFilters) => {
		const approvedUsers = users
			.filter((usr) => Number(usr?.user_id) !== 1)
			.filter(isApprovedUser);
		const adminUsers = approvedUsers.filter((usr) => {
			const allocatedIds = getUserAllocatedSiteIds(usr);
			return allocatedIds.length === 0 && isAdminRow(usr);
		});
		const adminIds = new Set(adminUsers.map((usr) => Number(usr.user_id)));
		const nonAdminUsers = approvedUsers.filter((usr) => !adminIds.has(Number(usr.user_id)));

		const siteIdsForScope = new Set(scopedSites.map((site) => Number(site.site_id)));
		const selectedSiteId = isAllValue(draftFilters.site) ? null : Number(draftFilters.site);
		const selectedCustomerId = isAllValue(draftFilters.customer) ? null : Number(draftFilters.customer);
		const customerSiteIds = selectedCustomerId
			? baseSites
				.filter((site) => Number(site.cus_id) === selectedCustomerId)
				.map((site) => Number(site.site_id))
			: [];

		const requiredSiteIds = selectedSiteId
			? new Set([selectedSiteId])
			: selectedCustomerId
				? new Set(customerSiteIds)
				: siteIdsForScope;

		const scopedUsers = nonAdminUsers.filter((usr) => {
			const userSiteIds = getUserAllocatedSiteIds(usr);
			if (userSiteIds.length === 0) return false;
			return userSiteIds.some((id) => requiredSiteIds.has(Number(id)));
		});

		const shouldIncludeAdmins = requiredSiteIds.size > 0;
		const combined = shouldIncludeAdmins ? [...adminUsers, ...scopedUsers] : scopedUsers;
		return getUserOptions(combined);
	};

	const validateSelections = (draftFilters, options) => {
		const isValid = (value, list) => isAllValue(value) || list.some((opt) => opt.value === value);
		const nextFilters = { ...draftFilters };
		if (!isValid(nextFilters.customer, options.customerOptions)) nextFilters.customer = ALL_VALUE;
		if (!isValid(nextFilters.site, options.siteOptions)) nextFilters.site = ALL_VALUE;
		if (!isValid(nextFilters.user, options.userOptions)) nextFilters.user = ALL_VALUE;
		if (!isValid(nextFilters.machine, options.machineOptions)) nextFilters.machine = ALL_VALUE;
		return nextFilters;
	};

	const applyAutoSelection = (draftFilters) => {
		const nextFilters = { ...draftFilters };
		if (!isAllValue(nextFilters.site)) {
			const selectedSite = baseSites.find((site) => Number(site.site_id) === Number(nextFilters.site));
			if (!selectedSite) {
				nextFilters.site = ALL_VALUE;
			} else if (isAllValue(nextFilters.customer)) {
				nextFilters.customer = selectedSite.cus_id ?? nextFilters.customer;
			}
		}
		if (!isAllValue(nextFilters.customer) && !isAllValue(nextFilters.site)) {
			const selectedSite = baseSites.find((site) => Number(site.site_id) === Number(nextFilters.site));
			if (selectedSite && Number(selectedSite.cus_id) !== Number(nextFilters.customer)) {
				nextFilters.site = ALL_VALUE;
			}
		}

		if (!isAllValue(nextFilters.machine)) {
			const candidateSites = getSitesForMachine(nextFilters.machine);
			const candidateSiteIds = new Set(candidateSites.map((site) => Number(site.site_id)));
			if (candidateSites.length === 0) {
				nextFilters.machine = ALL_VALUE;
			} else if (!isAllValue(nextFilters.site)) {
				if (!candidateSiteIds.has(Number(nextFilters.site))) {
					nextFilters.machine = ALL_VALUE;
				}
			} else if (!isAllValue(nextFilters.customer)) {
				const customerCandidates = candidateSites.filter(
					(site) => Number(site.cus_id) === Number(nextFilters.customer)
				);
				if (customerCandidates.length === 1) {
					nextFilters.site = customerCandidates[0].site_id;
					nextFilters.customer = customerCandidates[0].cus_id ?? nextFilters.customer;
				}
			} else if (candidateSites.length === 1) {
				nextFilters.site = candidateSites[0].site_id;
				nextFilters.customer = candidateSites[0].cus_id ?? nextFilters.customer;
			}
		}

		if (!isAllValue(nextFilters.user)) {
			const selectedUser = users.find((user) => Number(user.user_id) === Number(nextFilters.user));
			if (!selectedUser || !isApprovedUser(selectedUser)) {
				nextFilters.user = ALL_VALUE;
			} else if (!isAllValue(nextFilters.site)) {
				const userSiteIds = getAccessibleSiteIdsForUser(selectedUser, baseSites);
				if (!userSiteIds.includes(Number(nextFilters.site))) {
					nextFilters.user = ALL_VALUE;
				}
			}
		}

		return nextFilters;
	};

	const computeNextState = (inputFilters) => {
		let draftFilters = { ...inputFilters };
		for (let i = 0; i < 2; i += 1) {
			draftFilters = applyAutoSelection(draftFilters);
			const scopedSites = computeScopedSites(draftFilters);
			const options = buildOptionsFromScope(scopedSites);
			options.userOptions = getUserOptionsForScope(scopedSites, draftFilters);
			const validatedFilters = validateSelections(draftFilters, options);
			if (areFiltersEqual(draftFilters, validatedFilters)) {
				return { filters: validatedFilters, options };
			}
			draftFilters = validatedFilters;
		}
		const scopedSites = computeScopedSites(draftFilters);
		const options = buildOptionsFromScope(scopedSites);
		options.userOptions = getUserOptionsForScope(scopedSites, draftFilters);
		return { filters: draftFilters, options };
	};

	useEffect(() => {
		if (appliedSelectedSiteRef.current) return;
		if (!selectedSite || !baseSites.length) return;
		const selectedSiteRow = baseSites.find(
			(site) => String(site.customer_site_number) === String(selectedSite)
		);
		appliedSelectedSiteRef.current = true;
		if (!selectedSiteRow) return;
		setFilters((prev) => ({ ...prev, site: selectedSiteRow.site_id }));
	}, [selectedSite, baseSites]);

	useEffect(() => {
		const { filters: nextFilters, options } = computeNextState(filters);
		setCustomersOptions(withAllOption(options.customerOptions));
		setSitesOptions(withAllOption(options.siteOptions));
		setUsersOptions(withAllOption(options.userOptions));
		setMachinesOptions(withAllOption(options.machineOptions));
		if (!areFiltersEqual(filters, nextFilters)) {
			setFilters(nextFilters);
		}
	}, [
		baseSites,
		baseCustomers,
		machines,
		users,
		filters,
		withAllOption,
	]);

	// Handle Customer Change
	const handleCustomerChange = (_event, option) => {
		setButtonShow(true);
		const selectedCustomer = option?.value ?? ALL_VALUE;
		setMachineInput("");
		setFilters((prevFilters) => ({
			...prevFilters,
			customer: selectedCustomer,
			site: ALL_VALUE,
			user: ALL_VALUE,
			machine: ALL_VALUE,
		}));
	};

	// Handle Site Change
	const handleSiteChange = (_event, option) => {
		setButtonShow(true);
		const selectedSiteId = option?.value ?? ALL_VALUE;
		setMachineInput("");
		setFilters((prevFilters) => ({
			...prevFilters,
			site: selectedSiteId,
		}));
	};

	// Handle User Change
	const handleUserChange = (_event, option) => {
		setButtonShow(true);
		const selectedUserId = option?.value ?? ALL_VALUE;
		setFilters((prevFilters) => ({ ...prevFilters, user: selectedUserId }));
	};

	const handleMachineChange = (_event, option) => {
		setButtonShow(true);
		const selectedMachineId = option?.value ?? ALL_VALUE;
		setFilters((prevFilters) => ({
			...prevFilters,
			machine: selectedMachineId,
		}));
	};

	const handleClearFilters = () => {
		setFilters({
			customer: ALL_VALUE,
			site: ALL_VALUE,
			user: ALL_VALUE,
			region: t('MENU_ITEMS.ALL'),
			machine: ALL_VALUE,
		});

		setCustomerInput("");
		setSiteInput("");
		setUserInput("");
		setMachineInput("");
		setButtonShow(false);
	};

	const handleClearAllAppliedFilters = () => {
		handleClearFilters();
		setAdditionalFilters([]);
	};

	const normalizeFilterValue = (value) => (isAllValue(value) ? t('MENU_ITEMS.ALL') : value);
	const buildChartQuery = () => {
		const siteId = isAllValue(filters.site) ? null : Number(filters.site);
		const selectedSiteRow = siteId ? sites.find((site) => Number(site.site_id) === siteId) : null;
		const siteNumber =
			selectedSiteRow?.customer_site_number ||
			selectedSiteRow?.site_number ||
			null;
		const [rangeStart, rangeEnd] = Array.isArray(selectedDateRange) ? selectedDateRange : [];
		return {
			startDate: formatApiDate(rangeStart) || selectedRange.startDate,
			endDate: formatApiDate(rangeEnd) || selectedRange.endDate,
			mode: selectedRange.label,
			user: normalizeFilterValue(filters.user),
			site: normalizeFilterValue(filters.site),
			customer: normalizeFilterValue(filters.customer),
			machine: normalizeFilterValue(filters.machine),
			site_number: siteId ? siteNumber : null,
			additionalFilters: additionalFilters || [],
		};
	};
	// Handler for button clicks
	const handleButtonClick = (label, key) => {
		const currentDate = new Date();
		let startDate, endDate;

		switch (key) {
			case "T": // Today
				startDate = new Date(currentDate);
				endDate = new Date(currentDate);
				break;
			case "W": // Week
				startDate = getStartOfWeek(new Date(currentDate));
				endDate = getEndOfWeek(new Date(currentDate));
				break;
			case "Q": // Quarter
				startDate = getStartOfQuarter(new Date(currentDate));
				endDate = getEndOfQuarter(new Date(currentDate));
				break;
			case "M": // Month
				startDate = getStartOfMonth(new Date(currentDate));
				endDate = getEndOfMonth(new Date(currentDate));
				break;
			case "Y": // Year
				startDate = getStartOfYear(new Date(currentDate));
				endDate = getEndOfYear(new Date(currentDate));
				break;

			case t('MENU_ITEMS.ALL'): // Year
				const startOfTwoYearsAgo = new Date(currentDate);
				startOfTwoYearsAgo.setFullYear(currentDate.getFullYear() - 2); // Set start date to 2 years ago
				startDate = startOfTwoYearsAgo;
				endDate = new Date(currentDate);
				break;
			default: // Custom
				startDate = null;
				endDate = null;
				break;
		}

		setSelectedRange({
			label,
			startDate: startDate ? formatDate(startDate) : "",
			endDate: endDate ? formatDate(endDate) : "",
			key,
		});
		setSelectedDateRange([startDate, endDate]);
	};

	const chartQueryRef = useRef("");

	useEffect(() => {
		const data = buildChartQuery();
		const key = JSON.stringify(data);
		if (key === chartQueryRef.current) return;
		chartQueryRef.current = key;
		setLoading(true);
		setChartQueryFilter(data);
	}, [selectedRange, selectedDateRange, filters, additionalFilters, sites]);

	const boxRef = useRef(null);
	const [isSticky, setIsSticky] = useState(false);

	useEffect(() => {
		const handleScroll = () => {
			const box = boxRef.current;
			if (box) {
				const { top } = box.getBoundingClientRect();
				setIsSticky(top <= 75); // matches your `top: "75px"`
			}
		};

		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	// Permission gate for Analytics page
	const currentRoleName = String(
		user?.role?.role_name ||
		user?.user_role_name ||
		user?.user_role ||
		""
	).trim().toLowerCase();
	const canSeeAnalytics =
		user?.user_id === 1 ||
		(
			currentRoleName === "admin" &&
			enabledAdminModuleSet.has("ITEMS_TEXT.ANALYTICS")
		);
	if (!canSeeAnalytics) {
		return <NotAuthorized />;
	}

	return (
		<Box
			sx={{
				minHeight: "100vh",
				height: "100%",
				width: "100vw",
				display: "flex",
				justifyContent: "flex-start",
				alignItems: "center",
				flexDirection: "column",
				pb: 2,
			}}>
			<PageTitle title={t('TITLES.ANALYTIC')} />
			<Box
				ref={boxRef}
				sx={{
					width: "100%",
					position: "sticky",
					top: "75px",
					zIndex: 1051,
					background: isSticky ? "#fff" : "inherit",
					px: 5,
					boxShadow: isSticky ? "0px 4px 10px rgba(0,0,0,0.1)" : "none",
					paddingBottom: isSticky ? "15px" : "0px",
				}}>
				<Box className="date-range-container">
					<Box className="data-filters" data-tour="analytics-filters">
						<Box
							sx={{
								display: "flex",
								flexDirection: "row",
								justifyContent: "flex-start",
								width: "100%",
								maxWidth: "85%",
								gap: "10px",
							}}>
							<Autocomplete
								size="small"
								disablePortal
								disableClearable={false}
								options={displayedCustomerOptions}
								getOptionLabel={(opt) => opt?.label || ''}
								isOptionEqualToValue={(o, v) => o.value === v.value}
								filterOptions={(x) => x}
								onInputChange={(_, val, reason) => {
									setCustomerInput(val);
								}}
								inputValue={customerInput}
								value={customersOptions.find(o => o.value === filters.customer) || null}
								onChange={handleCustomerChange}
								sx={{ width: '100%' }}
								renderInput={(params) => (
									<TextField
										{...params}
										size="small"
										label={t('PLACEHOLDER.FILTER_BY_CUSTOMER')}
										placeholder={t('PLACEHOLDER.FILTER_BY_CUSTOMER')}
										variant="outlined"
										sx={{ width: '100%' }}
									/>
								)}
							/>

							<Autocomplete
								size="small"
								disablePortal
								disableClearable={false}
								disabled={false}
								options={displayedSiteOptions}
								getOptionLabel={(opt) => opt?.label || ''}
								isOptionEqualToValue={(o, v) => o.value === v.value}
								filterOptions={(x) => x}
								onInputChange={(_, val, reason) => {
									setSiteInput(val);
								}}
								inputValue={siteInput}
								value={sitesOptions.find(o => o.value === filters.site) || null}
								onChange={handleSiteChange}
								sx={{ width: '100%' }}
								renderInput={(params) => (
									<TextField
										{...params}
										size="small"
										label={t('PLACEHOLDER.FILTER_BY_SITE')}
										placeholder={t('PLACEHOLDER.FILTER_BY_SITE')}
										variant="outlined"
										sx={{ width: '100%' }}
									/>
								)}
							/>

							<Autocomplete
								size="small"
								disablePortal
								disableClearable={false}
								disabled={false}
								options={displayedUserOptions}
								getOptionLabel={(opt) => opt?.label || ''}
								isOptionEqualToValue={(o, v) => o.value === v.value}
								filterOptions={(x) => x}
								renderOption={(props, option) => (
									<li {...props}>
										<Tooltip title={option?.label || ""} placement="right">
											<Box sx={{ minWidth: 0 }}>
												<Typography
													variant="body1"
													noWrap
													sx={{ maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis" }}>
													{option?.displayName || option?.label || ""}
												</Typography>
												{option?.email && (
													<Typography
														variant="body2"
														color="text.secondary"
														noWrap
														sx={{ fontSize: "11px", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis" }}>
														{option.email}
													</Typography>
												)}
											</Box>
										</Tooltip>
									</li>
								)}
								onInputChange={(_, val, reason) => {
									setUserInput(val);
								}}
								inputValue={userInput}
								value={usersOptions.find(o => o.value === filters.user) || null}
								onChange={handleUserChange}
								sx={{ width: '100%' }}
								renderInput={(params) => (
									<TextField
										{...params}
										size="small"
										label={t('PLACEHOLDER.FILTER_BY_USER')}
										placeholder={t('PLACEHOLDER.FILTER_BY_USER')}
										variant="outlined"
										sx={{ width: '100%' }}
									/>
								)}
							/>
							<Autocomplete
								size="small"
								disablePortal
								disableClearable={false}
								disabled={false}
								options={displayedMachineOptions}
								getOptionLabel={(opt) => opt?.label || ''}
								isOptionEqualToValue={(o, v) => o.value === v.value}
								filterOptions={(x) => x}
								onInputChange={(_, val, reason) => {
									setMachineInput(val);
								}}
								inputValue={machineInput}
								value={machinesOptions.find(o => o.value === filters.machine) || null}
								onChange={handleMachineChange}
								sx={{ width: '100%' }}
								renderInput={(params) => (
									<TextField
										{...params}
										label={t('PLACEHOLDER.FILTER_BY_MACHINE')}
										placeholder={t('PLACEHOLDER.FILTER_BY_MACHINE')}
										variant="outlined"
										size="small"
										sx={{ width: '100%' }}
									/>
								)}
							/>
						</Box>
						{buttonShow && (
							<Button
								variant="outlined"
								size="small"
								endIcon={<FilterAltOffIcon />}
								onClick={handleClearFilters}>
								{t('BUTTON.CLEAR')}
							</Button>
						)}
					</Box>
					<Box className="date-range-buttons" data-tour="analytics-date-range">
						{/* <Button
								key="HomeButton"
								onClick={() => setShowCharts(true)}
								variant="outlined"
								sx={{
									...StyledButton,
									background: "transparent",
									color: (theme) => theme.palette.primary.main,
								}}>
								<HomeIcon />
							</Button> */}
						{buttons.map((button) => (
							<Button
								key={button.key}
								onClick={() => handleButtonClick(button.label, button.key)}
								variant="roundedGreyBgSquareContained"
								sx={{
									background: selectedRange.key === button.key ? (theme) => theme.palette.primary.main : (theme) => alpha(theme.palette.primary.light, 0.2),
									color: selectedRange.key === button.key ? "#fff" : (theme) => theme.palette.primary.main

								}}>
								{button.key}
							</Button>
						))}
						<Divider orientation="vertical" flexItem />
						<DateRange
							setSelectedRange={setSelectedRange}
							selectedRange={selectedRange}
							selectedDateRange={selectedDateRange}
							setSelectedDateRange={setSelectedDateRange}
						/>
					</Box>
				</Box>
				<Box
					className="date-range-container"
					sx={{ justifyContent: "space-between", px: 2, columnGap: "10px" }}>
					<Box className="date-ranges">
						<Typography variant="h6">{selectedRange.label}</Typography>
						<Typography variant="body1">
							{selectedRange.startDate} - {selectedRange.endDate}
						</Typography>
					</Box>
					<Box display={"flex"} flexDirection="row" justifyContent="flex-end" alignItems="center" rowGap={1}>
						{(additionalFilters.length > 0 || appliedDropdownFilters.length > 0) && (
							<>
								<Box
									display={"flex"}
									flexDirection="row"
									justifyContent="flex-end"
									alignItems="center"
									rowGap={1}>
									<FilterAltIcon color="primary" />
									<Typography variant="body1" color="primary" mr={2}>
										{t('UI_TEXT.APPLIED_FILTERS')} :{" "}
									</Typography>
								</Box>
								{appliedDropdownFilters.map((filter) => (
									<Box key={`dropdown-${filter.id}`} my={2} mr={1}>
										<Box
											sx={{
												backgroundColor: "#eee",
												width: "fit-content",
												padding: "5px 10px",
												display: "flex",
												flexDirection: "row",
												alignItems: "center",
												justifyContent: "space-between",
												borderRadius: "5px",
											}}>
											<Typography fontSize={"11px"}>
												{filter.label}
											</Typography>
										</Box>
									</Box>
								))}
								{additionalFilters.map((filter) => (
									<Box key={filter.id} my={2} mr={1}>
										<Box
											sx={{
												backgroundColor: "#eee",
												width: "fit-content",
												padding: "5px 10px",
												display: "flex",
												flexDirection: "row",
												alignItems: "center",
												justifyContent: "space-between",
												borderRadius: "5px",
											}}>
											<Typography key={filter.id} fontSize={"11px"}>
												{filter.chartName} - <b>{filter.name}</b>
											</Typography>
											<IconButton
												sx={{
													padding: "0px 2px",
													marginLeft: "5px",
												}}
												onClick={() =>
													setAdditionalFilters(
														additionalFilters.filter((f) => f.id !== filter.id)
													)
												}>
												<CloseIcon sx={{ fontSize: "12px" }} />
											</IconButton>
										</Box>
									</Box>
								))}
								<Box display={"flex"} justifyContent={"center"}>
									<Button
										variant="text"
										color="primary"
										onClick={() => {
											handleClearAllAppliedFilters();
										}}>
										<FilterAltOffIcon /> {t('TITLES.CLEAR_ALL_FILTERS')}
									</Button>
								</Box>
							</>
						)}
					</Box>
				</Box>
			</Box>

			{showCharts ? (
				<AnalyticsCharts
					loading={loading}
					chartFilters={chartQueryFilter}
					setLoading={setLoading}
					selectedRange={selectedRange}
					additionalFilters={additionalFilters}
					setAdditionalFilters={setAdditionalFilters}
					setShowCharts={setShowCharts}
					setSelectedMetric={setSelectedMetric}
					selectedMetric={selectedMetric}
				/>
			) : (
				<AnalyticsTables
					loading={loading}
					chartFilters={chartQueryFilter}
					setLoading={setLoading}
					selectedRange={selectedRange}
					additionalFilters={additionalFilters}
					setAdditionalFilters={setAdditionalFilters}
					setShowCharts={setShowCharts}
					setSelectedMetric={setSelectedMetric}
					selectedMetric={selectedMetric}
				/>
			)}
			<FilterBox additionalFilters={additionalFilters} setAdditionalFilters={setAdditionalFilters} />
		</Box>
	);
};

export default Analytics;